'use client';

import CloseIcon from '@mui/icons-material/Close';
import {
	type AlertColor,
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { gql, useMutation } from '@parkchirp/api-client';
import type { Dayjs } from 'dayjs';
import { Dispatch, SetStateAction, useState } from 'react';

import type { SetShowSnackbar } from '../../../types';

import { dayjs } from '../../../utils';

type EditDefaultRateFeeHistoryModalProps = {
	parkerId: string;
	feeHistoryId: number;
	feesHistory: {
		id: number;
		startDate: string;
		notes: string;
		price: string;
		hasBeenUsed: boolean;
		changeReason: string;
	}[];
	closeModal: () => Promise<void>;
	setShowSnackbar: SetShowSnackbar;
	facilityId: string;
	tenantSubscriptionId: string;
	setShowEditDefaultRateFeeHistoryModal: Dispatch<SetStateAction<boolean>>;
	fetchParkerFeeHistory: () => void;
	activeFeeHistoryPrice: string;
};

const CREATE_PARKER_FEE_HISTORY = gql`
    mutation createParkerFeeHistory(
        $parkerId: String!
        $feeHistoryInput: FeeHistoryInput!
        $tenantSubscriptionId: String
        $facilityId: String!
        $createCreditsFromRateChange: Boolean
    ) {
        createParkerFeeHistory(
            parkerId: $parkerId
            feeHistoryInput: $feeHistoryInput
            tenantSubscriptionId: $tenantSubscriptionId
            facilityId: $facilityId
            createCreditsFromRateChange: $createCreditsFromRateChange
        )
    }
`;

const UPDATE_PARKER_FEE_HISTORY = gql`
    mutation updateParkerFeeHistory(
        $parkerId: String!
        $feeHistoryInput: FeeHistoryInput!
        $tenantSubscriptionId: String
        $facilityId: String!
    ) {
        updateParkerFeeHistory(
            parkerId: $parkerId
            feeHistoryInput: $feeHistoryInput
            tenantSubscriptionId: $tenantSubscriptionId
            facilityId: $facilityId
        )
    }
`;

export const EditDefaultRateFeeHistoryModal = ({
	parkerId,
	feeHistoryId,
	feesHistory,
	setShowSnackbar,
	closeModal,
	facilityId,
	tenantSubscriptionId,
	setShowEditDefaultRateFeeHistoryModal,
	fetchParkerFeeHistory,
	activeFeeHistoryPrice,
}: EditDefaultRateFeeHistoryModalProps) => {
	const editMode = !!feeHistoryId;
	const selectedFeeHistory = feesHistory.find(({ id }) => id === feeHistoryId);
	const lastReferencedFeeHistory = !selectedFeeHistory ? feesHistory[feesHistory.length - 1] : selectedFeeHistory;
	const sixMonthsAgo = dayjs.utc().subtract(6, 'month');

	const minDate = dayjs.utc(lastReferencedFeeHistory.startDate).isAfter(sixMonthsAgo)
		? dayjs.utc(lastReferencedFeeHistory.startDate)
		: sixMonthsAgo;
	const [startDate, setStartDate] = useState<string>(
		selectedFeeHistory?.startDate || lastReferencedFeeHistory.startDate,
	);
	const [monthlyGrossPrice, setMonthlyGrossPrice] = useState<number | string>(
		Number(selectedFeeHistory?.price.replace(/[$,]/g, '')) || '',
	);
	const [notes, setNotes] = useState<string>(selectedFeeHistory ? selectedFeeHistory.notes : '');
	const [createCreditsFromRateChange, setCreateCreditsFromRateChange] = useState<boolean>(false);
	const [createParkerFeeHistory, { loading: createInProgress }] = useMutation(CREATE_PARKER_FEE_HISTORY);
	const [updateParkerFeeHistory, { loading: updateInProgress }] = useMutation(UPDATE_PARKER_FEE_HISTORY);
	const [changeReason, setChangeReason] = useState<string>(selectedFeeHistory ? selectedFeeHistory.changeReason : '');

	const loading = createInProgress || updateInProgress;

	const handleSubmit = async () => {
		if (monthlyGrossPrice === '' || Number(monthlyGrossPrice) < 0) {
			setShowSnackbar({
				message: 'Please enter a valid gross amount',
				severity: 'error',
				autoHideDuration: 6000,
			});
			return;
		}
		if (!changeReason) {
			setShowSnackbar({
				message: 'Please select a reason',
				severity: 'error',
				autoHideDuration: 6000,
			});
			return;
		}
		try {
			let message = `Successfully updated the parker's rate`;
			let severity: AlertColor = 'success';
			if (editMode) {
				const { hasBeenUsed, ...rest } = selectedFeeHistory;
				await updateParkerFeeHistory({
					variables: {
						parkerId,
						feeHistoryInput: {
							...rest,
							id: Number(selectedFeeHistory.id),
							notes,
							startDate,
							price: Number(monthlyGrossPrice) * 100,
							changeReason,
						},
						facilityId,
						tenantSubscriptionId,
					},
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
				});
			} else {
				const feeHistoryCopy = { ...feesHistory[feesHistory.length - 1] };
				const { id, hasBeenUsed, ...rest } = feeHistoryCopy;
				const { data } = await createParkerFeeHistory({
					variables: {
						parkerId,
						feeHistoryInput: {
							...rest,
							notes,
							startDate,
							price: Number(monthlyGrossPrice) * 100,
							changeReason,
						},
						facilityId,
						tenantSubscriptionId,
						createCreditsFromRateChange,
					},
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
				});
				message = data.createParkerFeeHistory;
				if (message.includes('admin')) {
					severity = 'warning';
				}
			}
			await fetchParkerFeeHistory();
			setShowSnackbar({
				message,
				severity,
				autoHideDuration: 6000,
			});
		} catch {
			setShowSnackbar({
				message: 'There was an error with your request',
				severity: 'error',
				autoHideDuration: 6000,
			});
		} finally {
			setShowEditDefaultRateFeeHistoryModal(false);
		}
	};
	const activeOrNextUpFeeHistoryPrice = activeFeeHistoryPrice
		? parseFloat(activeFeeHistoryPrice.replace(/[$,]/g, ''))
		: parseFloat(feesHistory[0]?.price?.replace(/[$,]/g, '') || '0');

	const newAmountIsGreaterThanCurrentFeeHistory = activeOrNextUpFeeHistoryPrice < (Number(monthlyGrossPrice) || 0);
	const startDateIsTodayOrFuture = startDate && dayjs(startDate).isSameOrAfter(dayjs(), 'day');

	return (
		<Grid padding='10px' container rowSpacing={3}>
			<Grid
				sx={{ paddingTop: '0px !important' }}
				display='flex'
				item
				xs={12}
				justifyContent='flex-end'
				alignItems='center'
			>
				<IconButton onClick={closeModal}>
					<CloseIcon />
				</IconButton>
			</Grid>
			<Grid sx={{ paddingTop: '0px !important' }} item xs={12}>
				<h3>Schedule Rate Change</h3>
			</Grid>
			<Grid item xs={6}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker<Dayjs>
						label='Start Date'
						value={dayjs.utc(startDate)}
						minDate={minDate}
						onChange={(value) => value && setStartDate(value.format())}
						slotProps={{
							textField: {
								inputProps: {
									'data-cy': 'rateHistory-startDate',
									disabled: true,
								},
								variant: 'outlined',
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid item xs={6}>
				<TextField
					label='New Gross Amount'
					value={monthlyGrossPrice}
					onChange={(e) => {
						const { value } = e.target;
						setMonthlyGrossPrice(value === '' ? '' : Number(value));
					}}
					variant='outlined'
					type='number'
					inputProps={{
						step: '0.01',
						min: '0',
						'data-cy': 'new-gross-amount',
					}}
					InputProps={{
						startAdornment: <InputAdornment position='start'>$</InputAdornment>,
					}}
					fullWidth
				/>
			</Grid>
			<Grid item xs={12}>
				<TextField
					select
					label='Reason'
					value={changeReason}
					required
					onChange={(e) => setChangeReason(e.target.value)}
					variant='outlined'
					fullWidth
					inputProps={{
						'data-cy': 'new-reason',
					}}
				>
					{[
						{ value: 'NEGOTIATED', label: 'Negotiated' },
						{ value: 'NO_REASON', label: 'No Reason' },
						{ value: 'POSTPONED', label: 'Postponed' },
						{ value: 'REDUCED', label: 'Reduced' },
						{ value: 'RESCINDED', label: 'Rescinded' },
					].map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</TextField>
			</Grid>
			<Grid item xs={12}>
				<div style={{ width: '100%' }}>
					<TextField
						label='Notes'
						multiline
						minRows={4}
						value={notes}
						onChange={(e) => setNotes(e.target.value.slice(0, 255))}
						inputProps={{
							maxLength: 255,
							'data-cy': 'notes',
						}}
						placeholder='Notes'
						fullWidth
						variant='outlined'
					/>
				</div>
			</Grid>
			{!startDateIsTodayOrFuture && !newAmountIsGreaterThanCurrentFeeHistory && (
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								checked={createCreditsFromRateChange}
								onChange={(e) => setCreateCreditsFromRateChange(e.target.checked)}
								color='primary'
								data-cy='create-credits-from-change'
							/>
						}
						label='Adjust invoices and create credits for previously paid invoices'
					/>
				</Grid>
			)}
			{startDateIsTodayOrFuture && (
				<span style={{ fontStyle: 'italic', fontWeight: 500, marginTop: '10px' }}>
					NOTE: Updating the start date to be today or in the future will not modify invoices that have
					already been generated for the current or next month. These invoices must be adjusted manually.
				</span>
			)}
			<Grid item xs={12}>
				<Button fullWidth onClick={handleSubmit} loading={loading}>
					Submit
				</Button>
			</Grid>
		</Grid>
	);
};
